import React, { useLayoutEffect, useRef, useState } from "react";
import dateformat from "dateformat";
import cx from "classnames";

import { IKImage } from "imagekitio-react";
import { useSelector } from "react-redux";
import {
  selectDomains,
  selectStoryboard,
  selectStoryboardLoading,
} from "src/redux/selectors/experiences/storyboardSettings";

import { useDraft } from "../siteSettings/useDraft";
import { canEdit } from "../../utils/permissions";
import { DomainResponse } from "src/types/experiences/storyboardSettings";
import Tooltip from "src/components/tooltip";
import Truncate from "src/components/truncate";
import styles from "./heroCard.module.scss";

const HeroCard: React.FC = () => {
  const storyboard = useSelector(selectStoryboard);
  const loading = useSelector(selectStoryboardLoading);
  const fetchedDomains = useSelector(selectDomains);
  const { draft, setField } = useDraft();

  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const sizerRef = useRef<HTMLSpanElement>(null);
  const [inputWidth, setInputWidth] = useState<number | null>(null);

  const urlRef = useRef<HTMLAnchorElement>(null);
  const [isUrlTruncated, setIsUrlTruncated] = useState(false);

  const nameRef = useRef<HTMLSpanElement>(null);
  const [isNameTruncated, setIsNameTruncated] = useState(false);

  const name = draft?.name ?? storyboard?.name ?? "";
  const editable = canEdit(draft?.role ?? storyboard?.role);

  useLayoutEffect(() => {
    if (!sizerRef.current) return;
    const measured = Math.min(
      500,
      Math.max(10, sizerRef.current.offsetWidth + 2)
    );
    setInputWidth(measured);
  }, [name, isHovered, isFocused]);

  useLayoutEffect(() => {
    const el = urlRef.current;
    if (!el) return;
    setIsUrlTruncated(el.scrollWidth > el.clientWidth);
  });

  useLayoutEffect(() => {
    const el = nameRef.current;
    if (!el) return;
    setIsNameTruncated(el.scrollWidth > el.clientWidth);
  });

  if (!storyboard || loading) return null;

  const currentDomain = (draft?.domain ??
    storyboard?.domain) as DomainResponse | undefined;
  const domains: DomainResponse[] = (() => {
    if (!currentDomain) return fetchedDomains;
    return fetchedDomains.some((d) => d.id === currentDomain.id)
      ? fetchedDomains
      : [currentDomain, ...fetchedDomains];
  })();
  const selectedDomain =
    domains.find((d) => d.id === currentDomain?.id) ?? domains[0];
  const page = draft?.page ?? storyboard.page;
  const aliasValue =
    draft?.page?.canonical ?? storyboard?.page?.canonical ?? "";
  const url = aliasValue ? `${selectedDomain?.url ?? ""}${aliasValue}` : "";
  const modifiedDate = draft?.modifiedDate ?? storyboard.modifiedDate;
  const lastUpdated = modifiedDate
    ? dateformat(new Date(modifiedDate), "d mmm yyyy")
    : null;
  const publishedDate = page?.lastPublished
    ? dateformat(new Date(page?.lastPublished), "d mmm yyyy")
    : null;

  const showNameInput = editable && (isHovered || isFocused);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!editable) return;
    setField("name", e.target.value);
  };

  return (
    <div className={styles.card}>
      <div className={styles.thumbnail}>
        {storyboard.page?.icon?.url ? (
          <IKImage
            path={storyboard.page?.icon?.url}
            alt={storyboard.name}
            className={styles.thumbnailImg}
          />
        ) : (
          <img
            src="/images/assets/default_asset.jpg"
            alt={storyboard.name}
            className={cx(styles.thumbnailImg, styles.defaultThumbnail)}
          />
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.nameRow}>
          <div className={styles.titleRow}>
            <div
              className={styles.title}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
            >
              {editable && (showNameInput || !name || name.trim() === "") ? (
                <div className={styles.addListForm}>
                  <input
                    className={styles.input}
                    style={{
                      width: inputWidth ?? "max-content",
                    }}
                    value={name}
                    placeholder="Add a storyboard name..."
                    onChange={handleChange}
                  />
                </div>
              ) : (
                <Tooltip
                  placement="top"
                  content={name}
                  disabled={!isNameTruncated}
                >
                  <span
                    ref={nameRef}
                    className={styles.nameDisplay}
                    style={{ width: inputWidth ?? "max-content" }}
                  >
                    <Truncate lines={1} width={500} className={styles.name}>
                      {name}
                    </Truncate>
                  </span>
                </Tooltip>
              )}
              <span ref={sizerRef} className={styles.inputSizer}>
                {name || "Add a storyboard name..."}
              </span>
            </div>
            {page?.published && (
              <span className={cx(styles.badge, styles.badgeLive)}>
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 8 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className={styles.dot}
                >
                  <circle
                    cx="4"
                    cy="4"
                    r="3.2"
                    fill="#E51058"
                    stroke="white"
                    stroke-width="1.6"
                  />
                </svg>
                Live
              </span>
            )}
            {page?.changes && (
              <span className={cx(styles.badge, styles.badgeDraft)}>Draft</span>
            )}
          </div>
          {!name && name.trim() === "" && (
            <div className={styles.error}>Please provide a name for your storyboard.</div>
          )}
          {url && page?.published && (
            <Tooltip
              placement="top"
              content={url}
              disabled={!isUrlTruncated}
              className={styles.urlTooltip}
            >
              <a
                ref={urlRef}
                className={styles.url}
                href={url}
                target="_blank"
                rel="noreferrer"
              >
                {url}
              </a>
            </Tooltip>
          )}
        </div>
        <div className={styles.meta}>
          {storyboard.role && <span>Role : {storyboard.role}</span>}
          {publishedDate && <span>Published on {publishedDate}</span>}
          {lastUpdated && <span>Last updated on {lastUpdated}</span>}
        </div>
      </div>
    </div>
  );
};

export default HeroCard;
